"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import { FormattedMessage, useIntl } from "react-intl";
import { motion } from "framer-motion";
import {
  FaArrowRight,
  FaCheck,
  FaChevronDown,
  FaCircleCheck,
  FaRegCreditCard,
  FaUser,
  FaUserGear,
} from "react-icons/fa6";
import { RiTeamFill } from "react-icons/ri";
import { trackCtaClick, trackEvent } from "@/app/providers/ObservabilityProvider";
import { useAppLink } from "@/lib/analytics/app-links";
import type {
  PlanPriceDisplay,
  PlanPricing,
  PlanPricingByKey,
} from "@/lib/pricing/catalog";
import { DEMO_URL } from "@/lib/demo";
import { CONTENT_WIDTH, SECTION_GUTTER } from "../widths";

type BillingCycle = "monthly" | "annual";
type PriceMode = "ht" | "ttc";

type Plan = {
  id: "indiv" | "pro" | "team";
  /** The key this card reads out of the catalogue. */
  catalogKey: keyof PlanPricingByKey;
  Icon: React.ComponentType<{ className?: string }>;
  featureCount: number;
  /** Filled badge + accent border, versus the muted treatment of the last card. */
  accent: boolean;
  cta: "trial" | "demo";
};

// Presentation only. The amounts used to sit here too (`monthlyHT: 8 | 12 | 18`), alongside a VAT
// rate and a flat 10 % annual discount this file applied itself — which is how the page went on
// advertising prices and a saving the back-office had already changed. Every figure now arrives
// from `GET /billing/catalog`, already derived.
const PLANS: Plan[] = [
  {
    id: "indiv",
    catalogKey: "individual",
    Icon: FaUser,
    featureCount: 7,
    accent: true,
    cta: "trial",
  },
  {
    id: "pro",
    catalogKey: "pro",
    Icon: FaUser,
    featureCount: 8,
    accent: true,
    cta: "trial",
  },
  {
    id: "team",
    catalogKey: "team",
    Icon: RiTeamFill,
    featureCount: 10,
    accent: false,
    cta: "demo",
  },
];

/** Feature lines a stacked card (below lg) shows before its « see more » button. */
const FEATURES_PREVIEW = 4;

const REASSURANCE = [
  { key: "payment", Icon: FaRegCreditCard },
  { key: "manage", Icon: FaUserGear },
  { key: "cancel", Icon: FaCircleCheck },
] as const;

/**
 * Picks the cycle a card should quote, falling back to the other one when a plan is sold on a
 * single cycle — Team has no yearly price, and its card must still show a figure.
 */
function selectPrice(
  pricing: PlanPricing,
  billing: BillingCycle,
): PlanPriceDisplay | null {
  return billing === "annual"
    ? (pricing.yearly ?? pricing.monthly)
    : (pricing.monthly ?? pricing.yearly);
}

/**
 * Reads the per-month figure off a served price, in the selected tax mode.
 *
 * A read, not a computation: `monthlyEquivalentCents` already spreads a yearly price over twelve
 * months server-side, and the tax-inclusive amounts already carry the catalogue's display rate.
 */
function monthlyAmount(price: PlanPriceDisplay, priceMode: PriceMode): number {
  return (
    (priceMode === "ttc"
      ? price.monthlyEquivalentCentsIncludingTax
      : price.monthlyEquivalentCents) / 100
  );
}

/** Reads the amount actually charged per billing period, in the selected tax mode. */
function periodAmount(price: PlanPriceDisplay, priceMode: PriceMode): number {
  return (
    (priceMode === "ttc" ? price.amountCentsIncludingTax : price.amountCents) /
    100
  );
}

function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  layoutId,
  size = "md",
}: {
  value: T;
  options: { value: T; labelId: string; labelValues?: Record<string, string | number> }[];
  onChange: (value: T) => void;
  ariaLabel: string;
  layoutId: string;
  size?: "sm" | "md";
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`flex flex-row w-fit max-w-full bg-blue-ulight rounded-full ${
        size === "md" ? "p-1" : "p-0.5"
      }`}
    >
      {options.map((option) => {
        const active = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            // 44 px tall on phones and tablets (touch targets); unchanged from lg.
            className={`relative group flex items-center justify-center rounded-full font-semibold transition-colors duration-150 cursor-pointer whitespace-nowrap max-lg:min-h-11 ${
              size === "md"
                ? "px-4 sm:px-7 py-2 sm:py-2.5 text-sm min-w-32 sm:min-w-40"
                : "px-3 py-1.5 text-xs max-lg:min-w-14 max-lg:text-sm"
            }`}
          >
            {active && (
              <motion.span
                layoutId={layoutId}
                className="absolute inset-0 bg-surface rounded-full shadow-sm"
                transition={{ type: "spring", stiffness: 400, damping: 35 }}
              />
            )}
            <span
              className={`relative z-10 transition-colors duration-150 ${
                active
                  ? "text-brand"
                  : "text-gray-main group-hover:text-text-main"
              }`}
            >
              <FormattedMessage id={option.labelId} values={option.labelValues} />
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * The amounts of one card: headline, struck-through monthly price and yearly total.
 *
 * Its own component rather than a block inside {@link PlanCard}: every branch below is about
 * which figure to show, and leaving them there made the card's control flow hard to follow for
 * reasons that have nothing to do with the card's layout.
 */
function PlanPrice({
  plan,
  pricing,
  billing,
  priceMode,
}: {
  plan: Plan;
  /** The plan's prices as the catalogue serves them, or null when it prices no plan at all. */
  pricing: PlanPricing | null;
  billing: BillingCycle;
  priceMode: PriceMode;
}) {
  const intl = useIntl();

  const selected = pricing ? selectPrice(pricing, billing) : null;
  // The headline is always a per-month figure, whichever cycle is selected: on the yearly cycle
  // the server already spread the price over twelve months.
  const price = selected ? monthlyAmount(selected, priceMode) : null;
  // The struck-through figure is the monthly cycle's own price, shown only when the yearly one
  // actually costs less. A plan sold monthly only has nothing to strike through.
  const monthlyPrice = pricing?.monthly ?? null;
  const showsYearlyDetail =
    billing === "annual" && selected !== null && selected.billingCycle === "yearly";
  const fullPrice =
    showsYearlyDetail && monthlyPrice !== null
      ? monthlyAmount(monthlyPrice, priceMode)
      : null;

  const formatPrice = (value: number) =>
    intl.formatNumber(value, {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    });

  return (
    <div className="px-6 sm:px-7 pt-5">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {fullPrice !== null && (
          <span className="text-lg line-through text-gray-main">
            {formatPrice(fullPrice)}
          </span>
        )}
        <span className="text-3xl xl:text-4xl font-bold text-brand">
          {price === null ? (
            <FormattedMessage id="pricing3.priceUnavailable" />
          ) : (
            formatPrice(price)
          )}
        </span>
        <span className="text-sm text-gray-main">
          <FormattedMessage id={`pricing3.${plan.id}.unit`} />
        </span>
      </div>
      <p className="mt-2 text-sm text-gray-main">
        <FormattedMessage
          id={showsYearlyDetail ? "pricing3.billedAnnually" : "pricing3.trial"}
        />
      </p>
      {showsYearlyDetail && selected !== null && (
        <p className="mt-1 text-xs text-gray-main">
          {/* The yearly total the customer is charged, as stored — not twelve times the
              monthly equivalent, which rounding can push a cent or two off. */}
          <FormattedMessage
            id="pricing3.annualTotal"
            values={{ total: formatPrice(periodAmount(selected, priceMode)) }}
          />
        </p>
      )}
    </div>
  );
}

function PlanCard({
  plan,
  pricing,
  billing,
  priceMode,
}: {
  plan: Plan;
  /** The plan's prices as the catalogue serves them, or null when it prices no plan at all. */
  pricing: PlanPricing | null;
  billing: BillingCycle;
  priceMode: PriceMode;
}) {
  const { Icon } = plan;
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const placement = `pricing-${plan.id}`;
  const { href: appUrl, target: appTarget } = useAppLink(placement);

  const ctaClasses = plan.accent
    ? "bg-blue-main text-white hover:bg-blue-fill"
    : // Text-safe blue on the tint (4.0:1 → 5.0:1), and a hover ground that keeps it ≥ 4.5:1.
      "bg-blue-ulight text-brand-ink hover:bg-blue-light";

  const ctaContent = (
    <>
      <FormattedMessage id={`pricing3.cta.${plan.cta}`} />
      <FaArrowRight aria-hidden="true" className="shrink-0 text-sm transition-transform duration-200 group-hover/cta:translate-x-1" />
    </>
  );

  const ctaClassName = `group/cta flex items-center justify-center gap-2 w-full text-center font-semibold py-3 px-4 rounded-full transition-colors duration-200 ${ctaClasses}`;

  return (
    // From lg the card becomes a subgrid of the section grid, so title, price,
    // CTA and feature list line up across the three cards whatever the copy length.
    <div
      className={`flex flex-col rounded-2xl overflow-hidden bg-surface border lg:grid lg:row-span-6 lg:grid-rows-subgrid ${
        plan.accent ? "border-blue-main" : "border-gray-light"
      }`}
    >
      {/* Badge */}
      <div
        className={`flex items-center justify-center text-center px-4 py-2.5 text-sm font-semibold ${
          plan.accent
            ? "bg-blue-main text-white"
            : "bg-blue-ulight text-funpurple"
        }`}
      >
        <FormattedMessage id={`pricing3.${plan.id}.badge`} />
      </div>

      {/* Title */}
      <div className="flex items-center gap-3 px-6 sm:px-7 pt-6">
        <Icon className="text-brand text-xl shrink-0" />
        <h3 className="text-2xl font-bold text-text-main">
          <FormattedMessage id={`pricing3.${plan.id}.title`} />
        </h3>
      </div>

      {/* Description */}
      <p className="px-6 sm:px-7 pt-3 text-sm text-gray-main">
        <FormattedMessage id={`pricing3.${plan.id}.desc`} />
      </p>

      {/* Price */}
      <PlanPrice
        plan={plan}
        pricing={pricing}
        billing={billing}
        priceMode={priceMode}
      />

      {/* CTA */}
      <div className="px-6 sm:px-7 pt-5">
        {plan.cta === "demo" ? (
          <a
            href={DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent(`pricing3-${plan.id}-button`, {}, { criticalOnNav: true });
              trackCtaClick(placement, "demo", { plan: plan.id });
            }}
            className={ctaClassName}
          >
            {ctaContent}
          </a>
        ) : (
          <Link
            href={appUrl}
            onClick={() => {
              trackEvent(`pricing3-${plan.id}-button`, {}, { criticalOnNav: true });
              trackCtaClick(placement, appTarget, { plan: plan.id });
            }}
            className={ctaClassName}
          >
            {ctaContent}
          </Link>
        )}
      </div>

      {/* Features: on phones and tablets the three cards stack, so each list opens on its
          first lines and unfolds on demand; from lg every line shows, side by side. */}
      <div>
        <ul id={`pricing-${plan.id}-features`} className="px-6 sm:px-7 pt-6 pb-6 sm:pb-7 flex flex-col gap-3">
          {Array.from({ length: plan.featureCount }, (_, index) => (
            <li
              key={index}
              className={`${!featuresOpen && index >= FEATURES_PREVIEW ? "hidden lg:flex" : "flex"} items-start gap-3`}
            >
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-ulight">
                <FaCheck className="text-[0.625rem] text-green-main" />
              </span>
              <span className="text-sm text-text-main">
                <FormattedMessage id={`pricing3.${plan.id}.f${index + 1}`} />
              </span>
            </li>
          ))}
        </ul>
        {plan.featureCount > FEATURES_PREVIEW && (
          <button
            type="button"
            aria-expanded={featuresOpen}
            aria-controls={`pricing-${plan.id}-features`}
            onClick={() => setFeaturesOpen((o) => !o)}
            className="-mt-3 mb-3 ml-3.5 flex min-h-11 cursor-pointer items-center gap-2 rounded-full px-2.5 text-sm font-semibold text-brand-ink hover:bg-blue-ulight sm:ml-4.5 lg:hidden"
          >
            <FaChevronDown aria-hidden="true" className={`text-xs transition-transform duration-200 ${featuresOpen ? "rotate-180" : ""}`} />
            <FormattedMessage
              id={featuresOpen ? "pricing3.featuresLess" : "pricing3.featuresMore"}
              values={{ count: plan.featureCount - FEATURES_PREVIEW }}
            />
          </button>
        )}
      </div>
    </div>
  );
}

export default function PricingSection({ pricing }: { pricing: PlanPricingByKey }) {
  const intl = useIntl();
  const [billing, setBilling] = useState<BillingCycle>("monthly");
  const [priceMode, setPriceMode] = useState<PriceMode>("ht");
  // `pricing-choice-made` means "the visitor engaged with the billing toggle": once per page view.
  const choiceTracked = useRef(false);

  // The saving advertised on the toggle is the best one the catalogue actually grants, read
  // back from the served rates. It used to be baked into the label ("Annuel −10 %"), which
  // survived every reprice.
  const yearlyDiscountPercent = Math.max(
    0,
    ...PLANS.map((plan) =>
      Math.round((pricing[plan.catalogKey]?.yearlyDiscountRate ?? 0) * 100),
    ),
  );

  return (
    <section id="pricing" aria-labelledby="pricing-title" className="w-full scroll-mt-24 bg-surface py-14 sm:py-20 2xl:py-28">
      <div className={`mx-auto ${CONTENT_WIDTH} ${SECTION_GUTTER}`}>
        {/* Header */}
        <div className="text-center flex flex-col items-center">
          <h2 id="pricing-title" className="text-[2rem] sm:text-5xl font-bold leading-[1.1] tracking-[-0.02em] text-title-blue text-balance 2xl:text-6xl">
            <FormattedMessage id="pricing3.title" />
            <span className="text-brand">.</span>
          </h2>
          <p className="mt-4 max-w-2xl text-base text-text-main text-balance 2xl:text-lg">
            <FormattedMessage id="pricing3.subtitle" />
          </p>
        </div>

        {/* Toggles */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <SegmentedControl
            value={billing}
            onChange={(value) => {
              trackEvent(`pricing-${value}-button`, {}, {});
              setBilling(value);
              if (!choiceTracked.current) {
                choiceTracked.current = true;
                trackEvent("pricing-choice-made", {}, {});
              }
            }}
            options={[
              { value: "monthly", labelId: "pricing3.monthly" },
              {
                value: "annual",
                labelId:
                  yearlyDiscountPercent > 0
                    ? "pricing3.annualWithDiscount"
                    : "pricing3.annual",
                labelValues: { percent: yearlyDiscountPercent },
              },
            ]}
            ariaLabel={intl.formatMessage({ id: "pricing3.billingCycle" })}
            layoutId="pricing-billing-pill"
          />
          <SegmentedControl
            value={priceMode}
            onChange={(value) => {
              trackEvent(`pricing-priceMode-${value}-button`, {}, {});
              setPriceMode(value);
            }}
            options={[
              { value: "ht", labelId: "pricing3.priceMode.ht" },
              { value: "ttc", labelId: "pricing3.priceMode.ttc" },
            ]}
            ariaLabel={intl.formatMessage({ id: "pricing3.priceMode" })}
            layoutId="pricing-priceMode-pill"
            size="sm"
          />
        </div>

        {/* Cards */}
        {/* Six rows — badge, title, description, price, CTA, features — shared by
            the three cards through `grid-rows-subgrid` from lg upwards. */}
        <div className="mt-10 grid grid-cols-1 gap-6 max-w-md mx-auto lg:max-w-none lg:grid-cols-3 lg:gap-y-0 lg:gap-x-6 xl:gap-x-8 lg:grid-rows-[auto_auto_auto_auto_auto_1fr]">
          {PLANS.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              pricing={pricing[plan.catalogKey] ?? null}
              billing={billing}
              priceMode={priceMode}
            />
          ))}
        </div>

        {/* Reassurance */}
        <ul className="mt-10 pt-6 border-t border-gray-light grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          {REASSURANCE.map(({ key, Icon }) => (
            <li
              key={key}
              className="flex items-start justify-center gap-2.5 text-sm text-text-main text-center sm:text-left"
            >
              <Icon className="mt-0.5 shrink-0 text-brand" />
              <span>
                <FormattedMessage id={`pricing3.reassurance.${key}`} />
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
